import type { CellEditorSelectorFunc, ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IReview, ML_LABEL } from '~/types/review'

import ActionMenu from '~/components/ActionMenu'
import { ReviewMlLabelStatusBadge, ReviewStatusBadge, ReviewTopicBadge } from '~/components/Badges'
import ReviewTopicCellEditor from '~/components/CellEditors/ReviewTopicCellEditor'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import { LANGUAGE_BY_LOCALE } from '~/const'
import { REVIEW_STATUS } from '~/types/review'
import { numberComparator } from '~/utils/comparators'
import { countriesTranslations } from '~/utils/countries'
import dayjs from '~/utils/dayjs'
import {
  actionsColDef,
  applicationColDef,
  dateColDef,
  ratingColDef,
  textAndTranslationColDef,
  textColDef,
} from '~/utils/table'

const ReviewsMlLabelCellRenderer = ({ value }: ICellRendererParams<IReview, IReview['ml_labels']>): ReactElement => {
  return (
    <div className="reviews-badge-cell-renderer">
      {value ? value.map((v, idx) => <ReviewMlLabelStatusBadge key={idx} value={v as ML_LABEL} />) : null}
    </div>
  )
}

const ReviewsStatusCellRenderer = ({ value }: ICellRendererParams<IReview, IReview['status']>): ReactElement => {
  return (
    <div className="reviews-badge-cell-renderer">
      {value ? [...new Set(value)].map((v, idx) => <ReviewStatusBadge key={idx} value={v as REVIEW_STATUS} />) : null}
    </div>
  )
}

const ReviewsTopicsCellRenderer = ({ value }: ICellRendererParams<IReview, IReview['topics']>): ReactElement => {
  return (
    <div className="reviews-badge-cell-renderer">
      {value ? value.map(({ label }, idx) => <ReviewTopicBadge key={idx} value={label} />) : null}
    </div>
  )
}

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<IReview>): ReactElement => {
  const { handleDoNotReport, handleOpenHistory, handleReply, handleReport, reviewsWriteAuthorized } = context

  const menu: Menu = {
    actions: [
      {
        action: handleReply(true, false, data),
        label: 'Reply',
        type: 'default',
      },
      {
        action: handleOpenHistory(true, data),
        label: 'View history',
        type: 'default',
      },
    ],
  }

  const isPendingReportOrReported =
    data?.status &&
    data.status.filter((status) =>
      [REVIEW_STATUS.PENDING_REPORT, REVIEW_STATUS.REPORTED].includes(status as REVIEW_STATUS)
    ).length > 0

  const isProposeToReportAndUntreatedReport =
    data?.status &&
    data.status.includes(REVIEW_STATUS.PROPOSE_REPORT) &&
    data.status.includes(REVIEW_STATUS.UNTREATED_REPORT)

  if (!isPendingReportOrReported) {
    menu.actions?.push({
      action: handleReport(data),
      label: 'Report',
      type: 'default',
    })
  }

  if (isProposeToReportAndUntreatedReport) {
    menu.actions?.push({
      action: handleDoNotReport(data),
      label: 'Do not report',
      type: 'default',
    })
  }

  return <ActionMenu disabled={!reviewsWriteAuthorized} menu={menu} placement="top" />
}

const cellEditorSelector: CellEditorSelectorFunc = (params) => {
  const colId = params.column.getColId()

  switch (colId) {
    case COLUMNS.TOPICS:
      return {
        component: ReviewTopicCellEditor,
        popup: false,
      }

    default:
      return undefined
  }
}

export const COLUMNS = {
  APPLICATION: 'application',
  APP_KEY: 'app_key',
  APP_VERSION: 'app_version',
  CONTENT: 'content&content_en',
  COUNTRY_CODE_LANGUAGE: 'country_code&language',
  CREATED_AT: 'created_at',
  DEVICE: 'device',
  LAST_REPLY: 'last_reply',
  ML_LABELS: 'ml_labels',
  RAM_MB: 'ram_mb',
  RATING: 'rating',
  REPLIES: 'replies',
  SDK_VERSION: 'sdk_version',
  STATUS: 'status',
  TITLE: 'title&title_en',
  TOPICS: 'topics',
  USERNAME: 'username',
}

const exportColDef: ColDef[] = [
  {
    field: COLUMNS.APP_KEY,
    hide: true,
    resizable: false,
    suppressMovable: true,
  },
  {
    field: 'content',
    hide: true,
  },
  {
    field: 'content_en',
    hide: true,
  },
  {
    field: 'country_code',
    hide: true,
  },
  {
    field: 'language',
    hide: true,
  },
  {
    field: 'title',
    hide: true,
  },
  {
    field: 'title_en',
    hide: true,
  },
  {
    field: COLUMNS.CREATED_AT,
    hide: true,
    valueGetter: ({ data }: ValueGetterParams<IReview>): string =>
      data?.created_at ? dayjs(data.created_at).format('YYYY-MM-DD') : '',
  },
  {
    field: 'last_answer_date',
    hide: true,
    valueGetter: ({ data }: ValueGetterParams<IReview>): string => {
      if (data?.replies && data?.replies.length > 0) {
        return dayjs(data?.replies[0].created_at).format('YYYY-MM-DD')
      }
      return ''
    },
  },
  {
    field: 'last_answer_content',
    hide: true,
    valueGetter: ({ data }: ValueGetterParams<IReview>): string => {
      if (data?.replies && data?.replies.length > 0) {
        return data.replies[0].content
      }
      return ''
    },
  },
  {
    field: 'topics_for_export',
    headerName: 'Topics',
    hide: true,
    valueGetter: ({ data }: ValueGetterParams<IReview>): string[] => {
      if (data?.topics && data.topics.length > 0) {
        return data?.topics.map((topic) => topic.label)
      }
      return []
    },
  },
]

const columns: ColDef[] = [
  ...exportColDef,
  applicationColDef({
    colId: COLUMNS.APP_KEY,
    field: COLUMNS.APPLICATION,
    minWidth: 130,
    valueGetter: ({
      data,
    }: ValueGetterParams<IReview & { app_icon_url: string; app_name: string }>): Record<string, string> => {
      const { app_icon_url = '', app_name = '', device_type = '' } = data || {}

      return {
        app_icon_url,
        device_type,
        name: app_name,
      }
    },
  }),
  dateColDef({
    field: COLUMNS.CREATED_AT,
    headerName: 'Date',
    suppressHeaderMenuButton: true,
    valueGetter: ({ data }: ValueGetterParams<IReview>): number => (data ? data.created_at * 1000000 : 0),
  }),
  textColDef(TextCellRenderer, {
    field: COLUMNS.COUNTRY_CODE_LANGUAGE,
    headerName: 'Country / Language',
    valueGetter: ({ data }: ValueGetterParams<IReview>): string | null => {
      if (data) {
        const { country_code, language } = data
        return country_code
          ? `${country_code.toLowerCase()} (${countriesTranslations[country_code.toUpperCase()] || '?'})`
          : `${language.toLowerCase()} (${LANGUAGE_BY_LOCALE[language] || '?'})`
      }
      return null
    },
  }),
  textColDef(TextCellRenderer, {
    field: COLUMNS.APP_VERSION,
    headerName: 'App version',
  }),
  textColDef(TextCellRenderer, {
    field: COLUMNS.USERNAME,
    headerName: 'Username',
  }),
  textAndTranslationColDef({
    field: COLUMNS.TITLE,
    headerName: 'Title translated',
    minWidth: 200,
    valueGetter: ({ data }: ValueGetterParams<IReview>): { original: string; translation: string } | null => {
      if (data) {
        const { title, title_en } = data
        return {
          original: title,
          translation: title_en,
        }
      }
      return null
    },
  }),
  textAndTranslationColDef({
    field: COLUMNS.CONTENT,
    headerName: 'Content translated',
    minWidth: 400,
    valueGetter: ({ data }: ValueGetterParams<IReview>): { original: string; translation: string } | null => {
      if (data) {
        const { content, content_en } = data
        return {
          original: content,
          translation: content_en,
        }
      }
      return null
    },
  }),
  ratingColDef({
    field: COLUMNS.RATING,
  }),
  {
    autoHeight: true,
    cellRenderer: ReviewsMlLabelCellRenderer,
    field: COLUMNS.ML_LABELS,
    headerName: 'Ml labels',
    minWidth: 200,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
  {
    autoHeight: true,
    cellEditorSelector: cellEditorSelector,
    cellRenderer: ReviewsTopicsCellRenderer,
    editable: true,
    field: COLUMNS.TOPICS,
    headerName: 'Topics',
    minWidth: 250,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
  {
    autoHeight: true,
    cellRenderer: ReviewsStatusCellRenderer,
    field: COLUMNS.STATUS,
    headerName: 'Statuses',
    minWidth: 200,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
  textAndTranslationColDef({
    field: COLUMNS.REPLIES,
    headerName: 'Last answer',
    minWidth: 400,
    valueGetter: ({ data }: ValueGetterParams<IReview>): { original: string; translation: string } | null => {
      if (data) {
        if (data && data.replies.length > 0) {
          const { replies } = data

          return {
            original: replies[0].content,
            translation: replies[0].content_en,
          }
        }
      }

      return null
    },
  }),
  dateColDef({
    field: COLUMNS.LAST_REPLY,
    headerName: 'Answer date',
    sortable: false,
    valueGetter: ({ data }: ValueGetterParams<IReview>): number => (data ? data.replies?.[0]?.created_at * 1000000 : 0),
  }),
  textColDef(TextCellRenderer, {
    field: COLUMNS.DEVICE,
    filter: 'agSetColumnFilter',
    headerName: 'Device',
    minWidth: 200,
  }),
  textColDef(TextCellRenderer, {
    comparator: numberComparator,
    field: COLUMNS.RAM_MB,
    headerName: 'Ram (MB)',
    valueGetter: ({ data }: ValueGetterParams<IReview>): number | undefined =>
      data?.ram_mb === 0 ? undefined : data?.ram_mb,
  }),
  textColDef(TextCellRenderer, {
    comparator: numberComparator,
    field: COLUMNS.SDK_VERSION,
    headerName: 'SDK version',
    valueGetter: ({ data }: ValueGetterParams<IReview>): number | undefined =>
      data?.sdk_version === 0 ? undefined : data?.sdk_version,
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
